import { ActionTree } from 'vuex';
import { AxiosInstance } from 'axios';
import { DateTime } from 'luxon';
import { DashboardState, RootState } from '../types';
import { handleAllErrors } from '@/utils/errorHandling';
import { state, PENDING_ORDERS_LIMIT, QUOTES_LIMIT } from '../dashboard/dashboard';
import { state as authState } from '../auth/auth';

export const actions: ActionTree<DashboardState, RootState> = {
	async fetchDashboardData({ commit }): Promise<{ areThereNewOrders: boolean, fetchDate: string } |void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data, headers } = await axiosInst.get<DashboardOrdersResponse>('/orders/dashboard', {
				params: {
					...state.filters,
					status: JSON.stringify(state.filters.status),
					type: JSON.stringify(state.filters.type),
					sortBy: state.pendingOrdersSort,
					pendingOrdersLimit: state.pendingOrdersLimit,
					quotesLimit: state.quotesLimit,
					includeModificationRequests: !!(authState.restaurant.catering && authState.restaurant.modify_order_requests),
					cateringEventsEnabled: !!(authState.restaurant.catering && authState.restaurant.catering_events)
				}
			});
			commit('SET_DASHBOARD_DATA', data);

			return { areThereNewOrders: data.newOrders, fetchDate: headers['date'] };
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async setFilters({ commit, dispatch }, filters: DashboardFilters): Promise<void> {
		commit('SET_FILTERS', filters);
		await dispatch('fetchDashboardData');
	},

	async resetDashboardFilters({ commit, dispatch }): Promise<void> {
		commit('SET_FILTERS', {
			...state.filters,
			startDate: DateTime.local().startOf('month').toISODate(),
			endDate: DateTime.local().endOf('month').toISODate(),
			status: [],
			type: [],
			timezone: authState.restaurant.menu_configuration?.zone
		});
		commit('SET_CALENDAR_TYPE', 'month');
		await dispatch('fetchDashboardData');
	},

	async fetchPendingOrders({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<DashboardOrder[]>('/orders/dashboard/pending-orders', {
				params: {
					sortBy: state.pendingOrdersSort,
					pendingOrdersOffset: state.pendingOrdersOffset,
					pendingOrdersLimit: PENDING_ORDERS_LIMIT
				}
			});
			commit('SET_PENDING_ORDERS', data);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async setPendingOrdersSort({ commit, dispatch }, sortBy: 'created_at' | 'pickup_time'): Promise<void> {
		commit('SET_PENDING_ORDERS_SORT', sortBy);
		await dispatch('fetchPendingOrders');
	},

	async setPendingOrdersOffset({ commit, dispatch }, offset: number): Promise<void> {
		commit('SET_PENDING_ORDERS_OFFSET', offset);
		// Increment limit so when fetchDashboardData polls it fetches the same amount of orders that are in the pendingOrders state
		commit('SET_PENDING_ORDERS_LIMIT', state.pendingOrdersLimit + PENDING_ORDERS_LIMIT);
		await dispatch('fetchPendingOrders');
	},

	async fetchQuotes({ commit }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<DashboardQuotes>('/orders/dashboard/quotes', {
				params: {
					quotesOffset: state.quotesOffset,
					quotesLimit: QUOTES_LIMIT
				}
			});
			commit('SET_QUOTES', data.rows);
			commit('SET_QUOTES_COUNT', data.count);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async setQuotesOffset({ commit, dispatch }, offset: number): Promise<void> {
		commit('SET_QUOTES_OFFSET', offset);
		commit('SET_QUOTES_LIMIT', state.quotesLimit + QUOTES_LIMIT);
		await dispatch('fetchQuotes');
	},

	setCalendarType({ commit }, calendarType: 'month' | 'week' | 'day'): void {
		commit('SET_CALENDAR_TYPE', calendarType);
	},

	async fetchCalendarSubscriptionUrl({ dispatch }): Promise<{ url: string, webcalUrl: string } | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<{ url: string, webcalUrl: string }>('/calendar/subscription');

			return data;
		}
		catch (error) {
			// 880 is the error code if a subscription url doesn't exist
			if ((error as any).response?.data?.code === 880) {
				return dispatch('createCalendarSubscriptionUrl');
			}
			handleAllErrors(error);
		}
	},

	async createCalendarSubscriptionUrl(): Promise<{ url: string, webcalUrl: string } | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.post<{ url: string, webcalUrl: string }>('/calendar/subscription');

			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	}
};